import { useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";

export type AppRole = "authenticated" | "host" | "admin";

interface RequireRoleProps {
  role: AppRole;
  children: React.ReactNode;
}

/**
 * Gate component that requires the user to have a specific role.
 *
 *   "authenticated" — any signed-in user (e.g. profile, registration)
 *   "host"          — user is admin OR has ≥1 location_host assignment
 *   "admin"         — user has 'admin' in person_role
 *
 * If not signed in, opens the sign-in dialog (Google or email) once and shows
 * a "Sign in" prompt behind it in case the user dismisses it.
 * If signed in but lacking the required role, shows access-denied.
 */
export function RequireRole({ role, children }: RequireRoleProps) {
  const { user, isLoading, isAdmin, isHost, setAuthModalOpen } = useAuth();
  const promptedRef = useRef(false);

  useEffect(() => {
    if (!isLoading && !user && !promptedRef.current) {
      promptedRef.current = true;
      setAuthModalOpen(true);
    }
  }, [isLoading, user, setAuthModalOpen]);

  if (isLoading) {
    return (
      <main className="container mx-auto px-4 md:px-8 py-16 md:py-20">
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      </main>
    );
  }

  if (!user) {
    return (
      <main className="container mx-auto px-4 md:px-8 py-16 md:py-20 max-w-lg text-center">
        <h1 className="text-2xl font-bold mb-3">Sign in to continue</h1>
        <p className="text-muted-foreground mb-6">
          You need to be signed in to view this page.
        </p>
        <Button onClick={() => setAuthModalOpen(true)}>
          <LogIn className="h-4 w-4 mr-2" aria-hidden="true" />
          Sign in
        </Button>
      </main>
    );
  }

  const allowed =
    role === "authenticated" ||
    (role === "admin" && isAdmin) ||
    (role === "host" && isHost);

  if (!allowed) {
    return (
      <main className="container mx-auto px-4 md:px-8 py-16 md:py-20 max-w-lg text-center">
        <h1 className="text-2xl font-bold mb-3">Access denied</h1>
        <p className="text-muted-foreground">
          You don't have permission to view this page. Signed in as{" "}
          <span className="font-medium text-foreground">{user.email}</span>.
        </p>
      </main>
    );
  }

  return <>{children}</>;
}
